import {
  CollctionDataType,
  CollectionMasterDataType,
  CollectionDataCache,
  ItemDataType,
  MasterDataType,
  OwnerData,
  ItemFileData,
  Attribute,
} from "../types";
import { BACKEND_URL } from "../config";
import { TelegramInitData } from "@/types/telegram";
import {
  Account,
  ConnectAdditionalRequest,
  TonProofItemReplySuccess,
} from "@tonconnect/sdk";
import { LSService } from "./storage";
import {
  CollectionTrackData,
  defaultIncomeData,
  HideCollectionRequestData,
  IncomeData,
  ItemInCollection,
  OwnerFileItem,
  OwnerRatingRow,
  PhotoCategories,
  ProfileExtendedInfo,
  ReferralCustomParams,
  ReferralStats,
  RoyaltyParams,
  SocialItem,
  UserProfileFullData,
  WalletListItem,
  WhitelistRefreshData,
} from "./types";
import BackendConnect from "./connect";
export * from "./types";

class BackendService extends BackendConnect {
  private static instance: BackendService;

  constructor(headers: Record<string, string>) {
    super(headers);
  }

  static getInstance(): BackendService {
    if (!BackendService.instance) {
      BackendService.instance = new BackendService({});
      const bearerToken = localStorage.getItem("bearerToken");
      if (bearerToken) {
        BackendService.instance.setAccessToken(bearerToken);
      }
    }
    return BackendService.instance;
  }

  public async getItemData(address: string): Promise<ItemDataType | null> {
    return new Promise(resolve => {
      this.get(`item/${address}`)
        .then(result => {
          resolve(result.data);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async getCollectionData(
    address: string
  ): Promise<CollectionDataCache | null> {
    return new Promise(resolve => {
      this.get(`collectiondata/${address}`)
        .then(result => {
          resolve(result.collection);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async setUserDisplayName(username: string): Promise<boolean> {
    return new Promise(resolve => {
      this.post(
        "usernameset",
        {
          username,
        },
        "POST"
      )
        .then(result => {
          resolve(!!result.success);
        })
        .catch(e => {
          console.log(e);
          resolve(false);
        });
    });
  }

  public async getUserDisplayName(address: string): Promise<string | null> {
    return new Promise(resolve => {
      this.get(`usernameget/${address}`)
        .then(result => {
          resolve(result.username);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async deleteOwnerPhoto(photoIds: number[]): Promise<boolean> {
    return new Promise(resolve => {
      this.post(
        "files/delete",
        {
          deletions: photoIds,
        },
        "POST"
      )
        .then(result => {
          resolve(!!result?.success);
        })
        .catch(e => {
          console.log(e);
          resolve(false);
        });
    });
  }

  public async checkWalletRights(
    proof: TonProofItemReplySuccess["proof"],
    account: Account
  ): Promise<string | null> {
    return new Promise(resolve => {
      this.post(
        "proofwallet",
        {
          address: account.address,
          proof: {
            address: account.address,
            network: account.chain,
            public_key: account.publicKey,
            proof: {
              ...proof,
              state_init: account.walletStateInit,
            },
          },
        },
        "POST"
      )
        .then(result => {
          resolve(result.success ? result.token : null);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async checkToken(token?: string): Promise<boolean> {
    console.log("Validation requested for:", token);
    const authToken = token || LSService.getTonAuthToken();
    if (!authToken) return false;
    return new Promise(resolve => {
      this.post(
        "validatetoken",
        {
          token: authToken,
        },
        "POST"
      )
        .then(result => {
          resolve(!!result.address);
        })
        .catch(e => {
          console.log(e);
          resolve(false);
        });
    });
  }

  public async generatePayload(): Promise<ConnectAdditionalRequest | null> {
    return new Promise(resolve => {
      this.get(`payloadtoken`)
        .then(result => {
          resolve({ tonProof: result.payload as string });
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async getOwnerData(
    address: string,
    view: "owner" | "user" = "user"
  ): Promise<OwnerData | null> {
    console.log("Owner data called for:", address, view);
    return new Promise(resolve => {
      this.get(`owner/${address}/${view}`)
        .then(result => {
          resolve(result);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async getItemsInCollection(data: {
    collectionAddress: string;
    page: number;
    perPage: number;
  }): Promise<ItemInCollection[]> {
    return new Promise(resolve => {
      const { collectionAddress, page, perPage } = data;
      this.get(`collectionitems/${collectionAddress}/${perPage}/${page}`)
        .then(result => {
          resolve(result.items);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }

  public async getMasterData(address: string): Promise<MasterDataType | null> {
    return new Promise(resolve => {
      this.get(`master/${address}`)
        .then(result => {
          resolve(result.data);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async getCollectionMasterData(
    address: string
  ): Promise<CollectionMasterDataType | null> {
    return new Promise(resolve => {
      this.get(`collectionmaster/${address}`)
        .then(result => {
          resolve(result.data);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async getCollectionRoyaltyParams(
    address: string
  ): Promise<RoyaltyParams | null> {
    return new Promise(resolve => {
      this.get(`collectionroyalty/${address}`)
        .then(result => {
          resolve(result.royalty);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async getDeployStatus(address: string): Promise<boolean> {
    return new Promise(resolve => {
      this.get(`isdeploy/${address}`)
        .then(result => {
          resolve(result.deploy || false);
        })
        .catch(e => {
          console.log(e);
          resolve(false);
        });
    });
  }

  public async registerReferral(
    address: string,
    referral: string
  ): Promise<boolean> {
    return new Promise(resolve => {
      this.get(`referralsave/${address}/${referral}`)
        .then(result => {
          resolve(result.success || false);
        })
        .catch(e => {
          console.log(e);
          resolve(false);
        });
    });
  }

  public async createCustomReferralLink(
    address: string
  ): Promise<string | null> {
    return new Promise(resolve => {
      this.get(`referrallink/${address}`)
        .then(result => {
          resolve(result.link || null);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async getWalletCollectionBalance(
    wallet: string,
    collection: string
  ): Promise<number> {
    return new Promise(resolve => {
      this.get(`collectionbalance/${wallet}/${collection}`)
        .then(result => {
          resolve(result.balance || 0);
        })
        .catch(e => {
          console.log(e);
          resolve(0);
        });
    });
  }

  public async getReferralCustomParams(
    address: string
  ): Promise<ReferralCustomParams> {
    return new Promise((resolve, reject) => {
      this.get(`referralcustom/${address}`)
        .then(result => {
          resolve(result);
        })
        .catch(e => {
          console.log(e);
          reject("Failed to get data");
        });
    });
  }

  public async getReferralStats(address: string): Promise<ReferralStats> {
    return new Promise((resolve, reject) => {
      this.get(`referralstats/${address}`)
        .then(result => {
          resolve(result);
        })
        .catch(e => {
          console.log(e);
          reject("Failed to get data");
        });
    });
  }

  public async getUserCollectionProfile(
    wallet: string,
    collection: string
  ): Promise<UserProfileFullData | null> {
    return new Promise(resolve => {
      this.get(`profile/${wallet}/${collection}`)
        .then(result => {
          resolve(result.profile as UserProfileFullData);
        })
        .catch(e => {
          console.log(e);
          resolve(null); // Возвращаем null при ошибке
        });
    });
  }

  public async getWhitelistStatus(
    wallet: string,
    collection: string
  ): Promise<boolean | null> {
    return new Promise(resolve => {
      this.get(`whitelist/${wallet}/${collection}`)
        .then(result => {
          resolve(result.is_whitelisted);
        })
        .catch(e => {
          console.log(e);
          resolve(null); // Если ошибка — возвращаем null
        });
    });
  }

  public async getCollectionWhitelist(filters: {
    collection: string;
    limit?: number;
    offset?: number;
    whitelisted?: boolean;
    search?: string;
  }): Promise<WalletListItem[]> {
    const {
      collection,
      limit = 100,
      offset = 0,
      whitelisted,
      search,
    } = filters;

    const query: Record<string, string | number> = {
      limit,
      offset,
    };

    if (whitelisted !== undefined) {
      query.whitelisted = String(whitelisted);
    }

    if (search !== undefined) {
      query.search = search;
    }

    try {
      const result = await this.get(
        `collection/whitelist/${collection}`,
        query
      );
      return result;
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  public async getCollectionWhitelistCount(
    collection: string,
    whitelisted?: boolean,
    search?: string
  ): Promise<number> {
    return new Promise(resolve => {
      let query: { whitelisted?: string; search?: string } = {};
      if (whitelisted !== undefined) {
        query = { whitelisted: String(whitelisted) };
      }
      if (search !== undefined) {
        query = { ...query, search };
      }
      this.get(`collection/whitelistcount/${collection}`, query)
        .then(result => {
          resolve(result?.count || 0);
        })
        .catch(e => {
          console.log(e);
          resolve(0);
        });
    });
  }

  public async getCollectionItemOwners(
    collection: string,
    pagination?: {
      limit: number;
      offset: number;
    }
  ): Promise<string[]> {
    return new Promise(resolve => {
      this.get(`collectionowners/${collection}`, pagination)
        .then(result => {
          resolve(result?.wallets || []);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }

  public async getCollectionUserItemIndexes(
    collection: string,
    wallet: string
  ): Promise<number[]> {
    return new Promise(resolve => {
      this.get(`collectionownernumbers/${collection}/${wallet}`)
        .then(result => {
          resolve(result?.numbers || []);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }

  public async refreshCollectionWhitelist(
    data: WhitelistRefreshData
  ): Promise<WalletListItem[]> {
    return new Promise(resolve => {
      this.post(`collection/massupdate`, data, "POST")
        .then(result => {
          resolve(result.whitelist || []);
        })
        .catch(e => {
          console.log(e);
          resolve([]); // On error null
        });
    });
  }

  public async getAddressByDomain(domain: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.get(`domainwallet/${domain}`)
        .then(result => {
          resolve(result.address);
        })
        .catch(e => {
          console.log(e);
          reject("Failed to get data");
        });
    });
  }

  public async getReferral(address: string): Promise<string | null> {
    return new Promise(resolve => {
      this.get(`referralget/${address}`)
        .then(result => {
          resolve(result.referral);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async saveTelegram(
    telegramInitData: string,
    wallet: string
  ): Promise<boolean> {
    return new Promise(resolve => {
      this.post(
        "user/telegramattach",
        {
          telegramInitData,
          wallet,
        },
        "POST"
      )
        .then(result => {
          resolve(result.success);
        })
        .catch(e => {
          console.log(e);
          resolve(false);
        });
    });
  }

  public async getAllLastCollections(): Promise<CollectionDataCache[]> {
    return new Promise(resolve => {
      this.get("home/lastcollections")
        .then(result => {
          resolve(result.collections);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }

  public async getAllLastCollectionsWithOffset(
    offset = 0,
    limit = 10,
    featured?: boolean,
    orderBy?: "stars" | "volume"
  ): Promise<CollectionDataCache[]> {
    return new Promise(resolve => {
      const params: Record<string, any> = {};
      if (featured !== undefined) {
        params.featured = !!featured;
      }
      if (orderBy) {
        params.orderby = orderBy;
      }

      this.get(
        `home/lastcollections/${offset}/${limit}`,
        Object.keys(params).length > 0 ? params : undefined
      )
        .then(result => {
          resolve(result.collections);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }

  public async getWalletDescription(address: string): Promise<string> {
    return new Promise(resolve => {
      this.get(`description/${address}`)
        .then(result => {
          resolve(result.description);
        })
        .catch(e => {
          console.log(e);
          resolve("");
        });
    });
  }

  public async updateWalletDescription(data: {
    address: string;
    text: string;
  }): Promise<boolean> {
    return new Promise(resolve => {
      this.post("updatedescription", data, "POST")
        .then(result => {
          resolve(result.success);
        })
        .catch(e => {
          console.log(e);
          resolve(false);
        });
    });
  }

  public async getOwnerPhotos(address: string): Promise<OwnerFileItem[]> {
    return new Promise(resolve => {
      this.get(`user/ownerphotos/${address}`)
        .then(result => {
          resolve(result.files);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }

  public async uploadOwnerPhoto(
    file: File,
    address: string,
    category: PhotoCategories
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // telegramInitData, address, category
      const formData = new FormData();
      formData.append("file", file);
      formData.append("address", address);
      formData.append("category", category);

      this.post("files/loaduserphoto", formData, "POST")
        .then(res => {
          resolve(!!res.success);
        })
        .catch(e => {
          console.log(e);
          reject(e.error || e);
        });
    });
  }

  public async setUserSocials(data: {
    address: string;
    socials: SocialItem[];
  }) {
    return new Promise((resolve, reject) => {
      this.post("user/updatesocials", data, "POST")
        .then(res => {
          resolve(!!res.success);
        })
        .catch(e => {
          console.log(e);
          reject(e.error || e);
        });
    });
  }

  public async getUserSocials(address: string): Promise<SocialItem[]> {
    return new Promise(resolve => {
      this.get(`user/socials/${address}`)
        .then(result => {
          resolve(result.socials);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }

  public async getUserRevenue(address: string): Promise<number> {
    return new Promise(resolve => {
      this.get(`turnower/${address}`)
        .then(result => {
          resolve(result.value);
        })
        .catch(e => {
          console.log(e);
          resolve(0);
        });
    });
  }

  public async getUserReferralRevenue(address: string): Promise<number> {
    return new Promise(resolve => {
      this.get(`referralturnower/${address}`)
        .then(result => {
          resolve(result.value);
        })
        .catch(e => {
          console.log(e);
          resolve(0);
        });
    });
  }

  public async getUserMarketIncome(address: string): Promise<number> {
    return new Promise(resolve => {
      this.get(`marketincome/${address}`)
        .then(result => {
          resolve(result.value);
        })
        .catch(e => {
          console.log(e);
          resolve(0);
        });
    });
  }

  public async getUserReferralMarketIncome(address: string): Promise<number> {
    return new Promise(resolve => {
      this.get(`referralmarketincome/${address}`)
        .then(result => {
          resolve(result.value);
        })
        .catch(e => {
          console.log(e);
          resolve(0);
        });
    });
  }

  public async getUserAllFinanceData(address: string): Promise<IncomeData> {
    return new Promise(resolve => {
      this.get(`allfinance/${address}`)
        .then(result => {
          resolve(result.values);
        })
        .catch(e => {
          console.log(e);
          resolve(defaultIncomeData);
        });
    });
  }

  public categoryName(id: number) {
    const names = [
      {
        id: 1,
        name: "background",
      },
      {
        id: 2,
        name: "avatar",
      },
    ];
    return names.find(item => item.id === id)?.name;
  }

  public async getTrackingCollcetions({
    category,
  }: {
    category?: number;
  }): Promise<CollectionTrackData[]> {
    return new Promise(resolve => {
      this.get(`skins/all`)
        .then(result => {
          resolve(
            category
              ? result.collections.filter(item => item.category_id === category)
              : result.collections
          );
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }

  public async getAvailableTrackingCollcetions(
    owner: string
  ): Promise<CollectionTrackData[]> {
    return new Promise(resolve => {
      this.get(`skins/available/${owner}`)
        .then(result => {
          resolve(result.collections);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }
  // returning nft address
  public async getSelectedSkinNft(
    owner: string,
    category: "background" | "avatar" = "background"
  ): Promise<string | null> {
    return new Promise(resolve => {
      const category_id = category === "background" ? 1 : 2;
      this.get(`userskin/${category_id}/${owner}`)
        .then(result => {
          resolve(result.item);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async getNftContentByDataUrl(
    url: string
  ): Promise<ItemFileData | null> {
    return new Promise(resolve => {
      fetch(url)
        .then(res => res.json())
        .then((res: ItemFileData) => {
          resolve(res);
        })
        .catch(() => {
          resolve(null);
        });
    });
  }

  public async hideCollectionByOwner(
    data: HideCollectionRequestData
  ): Promise<{ success: boolean; error?: string }> {
    return this.post("user/hidecollection", { ...data }, "POST");
  }
  // returning pure image
  public async getSelectedSkinNftFile(
    owner: string,
    category: "background" | "avatar" = "background"
  ): Promise<string | null> {
    return new Promise(resolve => {
      const category_id = category === "background" ? 1 : 2;
      this.get(`userskinfile/${category_id}/${owner}`)
        .then(async result => {
          if (!result.link) {
            resolve(null);
            return;
          }
          const nftData = await this.getNftContentByDataUrl(result.link);
          if (!nftData) {
            resolve(null);
            return;
          }
          resolve(nftData.image);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async selectUserSkin(nftAddress: string): Promise<boolean> {
    console.log("Register slkin called for:", nftAddress);
    return new Promise(resolve => {
      this.post("userskin/register", { item: nftAddress }, "POST")
        .then(res => {
          resolve(!!res.success);
        })
        .catch(e => {
          console.log(e);
          resolve(false);
        });
    });
  }

  public async deSelectUserSkin(
    category: "avatar" | "background"
  ): Promise<boolean> {
    return new Promise(resolve => {
      this.post(
        "userskin/deselect",
        { category_id: category === "background" ? 1 : 2 },
        "POST"
      )
        .then(res => {
          resolve(!!res.success);
        })
        .catch(e => {
          console.log(e);
          resolve(false);
        });
    });
  }

  public async getUserExtendedProfile(
    addesss: string
  ): Promise<ProfileExtendedInfo | null> {
    return new Promise(resolve => {
      this.get(`profilefull/${addesss}`)
        .then(res => {
          resolve(res.profile || null);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async getAddressItemBalance(
    address: string,
    item: "experience" | "stars"
  ): Promise<number> {
    return new Promise(resolve => {
      const itemId =
        item === "experience" ? 1 : item === "stars" ? 2 : undefined;
      this.get(`store/balance/${address}/${itemId}`)
        .then(result => {
          resolve(result.balance);
        })
        .catch(e => {
          console.log(e);
          resolve(0);
        });
    });
  }

  public async getTopWalletsRating(filters: {
    param: "volume" | "stars" | "experience";
    limit: number;
    offset: number;
  }): Promise<OwnerRatingRow[]> {
    const { param, limit, offset } = filters;
    return new Promise(resolve => {
      this.get(`wallet/rating/${param}/${limit}/${offset}`)
        .then(result => {
          resolve(result);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    });
  }

  public async getWalletRatingPosition(
    address: string,
    param: "volume" | "stars" | "experience"
  ): Promise<number | null> {
    // null - not presented in rating
    return new Promise(resolve => {
      this.get(`wallet/position/${param}/${address}`)
        .then(result => {
          resolve(result.position);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });
  }

  public async initStarPayment(data: {
    chatId: number;
    address: string;
    count: number;
  }): Promise<{ success: boolean }> {
    const { chatId, address, count } = data;
    return new Promise(async resolve => {
      try {
        const tg = window.Telegram?.WebApp;
        const isMiniApp =
          typeof window !== "undefined" &&
          tg &&
          !!tg.initData &&
          tg.platform !== "unknown";

        if (isMiniApp && window.Telegram) {
          const WebApp = window.Telegram.WebApp;

          const linkRequestBody = {
            makeUrl: true,
            amount: count,
            address,
            chatId
          };

          const { link } = await this.post("store/initstarpayment", linkRequestBody);

          WebApp.openInvoice(link, (status: string) => {
            if (status === "failed" || status === "cancelled") {
              resolve({ success: false });
            }

            if (status === "paid") {
              resolve({ success: true });
            }
          });
        } else {
          this.get(`store/initpayment/${address}/${chatId}/${count}`)
            .then(result => {
              resolve(result);
            })
            .catch(e => {
              console.log(e);
              resolve({ success: false });
            });
        }
      } catch (e) {
        console.error(e);
        resolve({ success: false });
      }
    });
  }

  public async getCollectionItemAttributes( address: string): Promise<Attribute[]> {
   return new Promise(resolve => {
      this.get(`collection/itemattributes/${address}`)
        .then(result => {
          resolve(result);
        })
        .catch(e => {
          console.log(e);
          resolve([]);
        });
    }); 
  }

  public async checkCookieAuth (): Promise<string | null | undefined> {
   return new Promise(resolve => {
      this.get("/checkauthcookie")
        .then(result => {
          console.log("Resolved from cookie:", result)
          resolve(result.address);
        })
        .catch(e => {
          console.log(e);
          resolve(null);
        });
    });     
  }
}

export const backend = BackendService.getInstance();
